import { getConsentSnapshot, subscribeConsent } from '@/contexts/consent/store';

type RumContext = Record<string, unknown>;
export type RumDurationOptions = {
    startTime: number;
    duration: number;
    context?: RumContext;
};

interface RumApi {
    addAction(name: string, context: RumContext): void;
    addError(error: unknown, context: RumContext): void;
    addDurationVital(name: string, options: RumDurationOptions): void;
    startView(options: { name: string }): void;
}

let api: RumApi | null = null;
let identityRevision = 0;
let identity: string | null = null;
let unsubscribeConsent: (() => void) | undefined;
const invalidationListeners = new Set<() => void>();
let view: { pathname: string; name: string } | undefined;
let observedViewKey: string | undefined;
let viewRevision = 0;
let startedViewKey: string | undefined;

function analyticsAllowed(): boolean {
    return getConsentSnapshot().analyticsAllowed;
}

function ensureConsentSubscription(): void {
    if (unsubscribeConsent || typeof window === 'undefined') return;
    let previouslyAllowed = analyticsAllowed();
    unsubscribeConsent = subscribeConsent(() => {
        const allowed = analyticsAllowed();
        if (previouslyAllowed && !allowed) invalidateRumIdentity();
        if (!previouslyAllowed && allowed) startCurrentView();
        previouslyAllowed = allowed;
    });
}

/** 本地失效版本也覆盖尚未授权的首次成功计时，不存储待发身份或业务事件。 */
export function captureRumIdentityRevision(): number {
    ensureConsentSubscription();
    return identityRevision;
}

export function captureRumGeneration(): number | null {
    if (typeof window === 'undefined' || !analyticsAllowed()) return null;
    ensureConsentSubscription();
    return identityRevision;
}

export function isRumGenerationCurrent(generation: number): boolean {
    return analyticsAllowed() && generation === identityRevision;
}

export function subscribeRumGenerationInvalidation(
    generation: number,
    onInvalidated: () => void,
): () => void {
    if (!isRumGenerationCurrent(generation)) {
        onInvalidated();
        return () => undefined;
    }
    invalidationListeners.add(onInvalidated);
    return () => { invalidationListeners.delete(onInvalidated); };
}

function invalidateRumIdentity(): void {
    identityRevision += 1;
    const listeners = [...invalidationListeners];
    invalidationListeners.clear();
    for (const listener of listeners) {
        try { listener(); }
        catch { console.warn('[datadog-rum] operation cleanup failed'); }
    }
}

export function updateRumIdentity(id: string): void {
    if (identity !== null && identity !== id) invalidateRumIdentity();
    identity = id;
}

export function resetRumIdentity(): void {
    invalidateRumIdentity();
    identity = null;
}

export function currentRumPage(): string {
    return typeof window !== 'undefined' && view?.pathname === window.location.pathname ? view.name : '/';
}

function currentViewKey(): string {
    const pathname = typeof window === 'undefined' ? '/' : window.location.pathname;
    return `${pathname}:${currentRumPage()}`;
}

export function captureRumViewRevision(): number {
    const key = currentViewKey();
    if (observedViewKey !== key) {
        observedViewKey = key;
        viewRevision += 1;
    }
    return viewRevision;
}

export function isRumViewRevisionCurrent(revision: number): boolean {
    return revision === captureRumViewRevision();
}

function startCurrentView(): void {
    if (!view || !api || !analyticsAllowed()) return;
    const key = `${identityRevision}:${view.pathname}:${view.name}`;
    if (key === startedViewKey) return;
    try {
        api.startView({ name: view.name });
        startedViewKey = key;
    } catch {
        console.warn('[datadog-rum] view update failed');
    }
}

/** 唯一 Next 路由适配器提供规范名；先结算旧访问，再切换 SDK view。 */
export function setRumViewName(name: string): void {
    if (typeof window === 'undefined') return;
    view = { pathname: window.location.pathname, name };
    captureRumViewRevision();
    startCurrentView();
}

/** 只注册框架已初始化的实例，业务调用不会加载或等待第二套 SDK。 */
export function registerRumApi(sdk: RumApi): void {
    api = sdk;
    ensureConsentSubscription();
    startCurrentView();
}

function report(context: RumContext, send: (sdk: RumApi) => void): Promise<void> {
    if (!api || captureRumGeneration() === null) return Promise.resolve();
    if (typeof context.page === 'string' && context.page !== view?.name) return Promise.resolve();
    try { send(api); }
    catch { console.warn('[datadog-rum] operation failed'); }
    return Promise.resolve();
}

export function reportRumAction(name: string, context?: RumContext): Promise<void> {
    const eventContext = { page: currentRumPage(), ...context };
    return report(eventContext, sdk => sdk.addAction(name, eventContext));
}

export function reportRumError(error: unknown, context?: RumContext): Promise<void> {
    const eventContext = { page: currentRumPage(), ...context };
    return report(eventContext, sdk => sdk.addError(error, eventContext));
}

/** SDK 接收 epoch 毫秒和已锁定的成功毫秒，不在这里转换为入库纳秒。 */
export function reportRumDuration(
    name: string,
    options: RumDurationOptions,
    isValid: () => boolean = () => true,
): Promise<void> {
    if (!Number.isFinite(options.startTime) || options.startTime < 0
        || !Number.isFinite(options.duration) || options.duration < 0) return Promise.resolve();
    const vital = { ...options, context: { page: currentRumPage(), ...options.context } };
    return report(vital.context, sdk => {
        if (isValid()) sdk.addDurationVital(name, vital);
    });
}
